import { EventDataType } from '@/types/EventDataType.type';
import { endPoint, getEventDateLabel, serverFetch } from '@/utils';
import { notFound } from 'next/navigation';
import { Suspense } from 'react';
import { MainSectionSkeleton } from '../mainSectionSkeleton/mainSectionSkeleton.component';
import {
  AboutEvent,
  ArtistCarousel,
  EventCarousel,
  EventGallery,
  EventSpecs,
  GetDirections,
  PoliciesAccordion,
  ShareEvent,
} from './components';

const MainSection = async ({ eventCode }: { eventCode: string }) => {
  let data: EventDataType;
  try {
    data = await serverFetch<EventDataType>({
      endpoint: endPoint.FETCH_EVENT_INFO,
      queryParams: { eventCode },
    });
  } catch {
    notFound();
  }

  // Shared IST-based label — identical to the event-info card's date.
  const dateLabel = getEventDateLabel(data!);

  return (
    <Suspense fallback={<MainSectionSkeleton />}>
      <article className='w-full pt-4 sm:pt-6'>
        {/* Event name first — the page <h1>, top of the content for SEO */}
        <header className='mx-auto flex w-full max-w-7xl flex-col gap-2 px-4 md:px-6'>
          <h1 className='text-2xl leading-tight font-bold tracking-tight text-[#101828] md:text-3xl'>
            {data!.name}
          </h1>

          {(dateLabel || data?.eventAddress) && (
            <p className='flex items-center gap-x-2 text-sm md:text-base'>
              {dateLabel && (
                <span className='shrink-0 font-semibold whitespace-nowrap text-primary'>
                  {dateLabel}
                  {data?.eventTime ? `, ${data.eventTime}` : ''}
                </span>
              )}
              {dateLabel && data?.eventAddress && (
                <span className='shrink-0 text-gray-300'>|</span>
              )}
              {data?.eventAddress && (
                <span className='min-w-0 truncate font-medium text-[#475467]'>
                  {data.eventAddress}
                </span>
              )}
            </p>
          )}
        </header>
        <div className='mx-auto mt-4 grid w-full max-w-7xl grid-cols-1 gap-6 px-4 pb-24 md:mt-6 md:px-6 lg:grid-cols-5 lg:items-start lg:gap-8 lg:pb-10'>
          {/* ---------- Main column ---------- */}
          <div className='flex flex-col gap-6 lg:col-span-3'>
            <EventCarousel
              posters={data!.poster}
              eventVideoLink={data!.eventVideoLink}
            />
            {/* Booking card — inline on mobile/tablet */}
            <div className='block lg:hidden'>
              <EventSpecs
                eventId={data?._id}
                data={data}
                eventCode={eventCode}
              />
            </div>

            <AboutEvent description={data!.description} />
            <PoliciesAccordion policies={data!.policies} />
            <ArtistCarousel artists={data!.artists} />
            <EventGallery
              images={data!.poster}
              isVideoAvailable={Boolean(data!.eventVideoLink)}
            />
          </div>

          {/* ---------- Sticky sidebar (desktop) ---------- */}
          <aside className='flex flex-col gap-6 lg:sticky lg:top-20 lg:col-span-2 lg:self-start'>
            <div className='hidden lg:block'>
              <EventSpecs
                eventId={data?._id}
                data={data}
                eventCode={eventCode}
              />
            </div>

            <GetDirections
              eventAddress={data!.eventAddress}
              location={data!.location}
            />

            {data?.ctaRedirectUrl && (
              <ShareEvent
                facebookLink={data.ctaRedirectUrl}
                instagramLink={data.ctaRedirectUrl}
                whatsappLink={data.ctaRedirectUrl}
                title={data.name}
                subtitle={data.description}
                seoDesc={data.seo.desc}
              />
            )}
          </aside>
        </div>
      </article>
    </Suspense>
  );
};

export default MainSection;
